import { useContext, useState } from "react"
import { Divider, EmailPreviewModal, LineLoader } from "@components"
import { NotificationContext } from "@context"
import { getDateRangeStatus, formattedDateRange } from "@helpers"
import { useDeleteEvent, useInvitationsSentCount, usePreviewEventCancellationEmail } from "@hooks"
import { Button, Checkbox, Textarea } from "@heroui/react"
import PropTypes from "prop-types"

const DeleteEvent = ({ backButton, event }) => {
  /**
   * State
   */
  const [sendCancellationEmail, setSendCancellationEmail] = useState(true)

  const [intro, setIntro] = useState(
    `We're sorry to let you know that this event scheduled for ${formattedDateRange(event.start_date, event.end_date)} has been cancelled.\n\n` + 
    `We apologise for any inconvenience this causes.\n\n` +
    `If you have questions, please contact us.\n\n` +
    `Thank you for your understanding.`
  )
  
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false)
  
  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)
  
  /**
   * Hooks
   */  
  const {deleteEvent, loading: isDeletingEvent} = useDeleteEvent()

  const {
    data: invitationCounts, 
    loading: isLoadingInvitations, 
  } = useInvitationsSentCount(event.id)

  const {
    getPreviewEmail, 
    data: previewEmailData, 
    loading: isLoadingEmailPreview, 
    error: previewEmailError
  } = usePreviewEventCancellationEmail()

  /**
   * Functions
   */
  const handleDeleteEvent = (isCancellationEmailShown) => {
    deleteEvent({
      variables: {
        input: {
          event_id: event.id,
          send_cancellation_emails: isCancellationEmailShown && sendCancellationEmail,
          cancellation_email_intro: intro,
        }
      }
    })
    .then((response) => {
      if (response?.data?.deleteEvent?.success) {
        window.location.replace("/")
      } else {
        notify({ title: "Error", type: "danger", content: `Couldn't delete event`, open: true })
      }
    })
    .catch((error) => {
      notify({ title: "Error", type: "danger", content: `Couldn't delete event`, open: true })
    })
  }

  const handlePreviewEmail = (intro) => {
    getPreviewEmail({
      variables: {
        input: { 
          event_id: event.id, 
          cancellation_email_intro: intro 
        }
      }
    })
    .then(() => setShowEmailPreviewModal(true))
  }
  
  const status = getDateRangeStatus(event.start_date, event.end_date)  
  const noOfInvitationsSent = invitationCounts?.event?.no_of_invitations_sent || 0 
  const isEventCurrentOrUpcoming = ["Current", "Upcoming"].includes(status)
  const isFormValid = intro.length >= 150 && intro.length <= 1000 ? true : false
  const isDeleteEventButtonDisabled = sendCancellationEmail && noOfInvitationsSent > 0 && isEventCurrentOrUpcoming ? !isFormValid : false
  const isPreviewEmailButtonDisabled = !sendCancellationEmail || !isFormValid
  const isCancellationEmailShown = isEventCurrentOrUpcoming && noOfInvitationsSent > 0 && !isLoadingInvitations

  if (isLoadingInvitations) {
    return (
      <LineLoader message="Please wait loading..." classNames="mt-8" />
    )
  }
  
  return (
    <div>      
      <p className="text-base text-danger">
        Are you sure you wish to delete this event? Any bookings & invitations will be deleted. This cannot be undone!
      </p>

      <Divider className="my-4" />
      
      {isCancellationEmailShown && (
        <div>
          <div className="flex items-center justify-between w-full mb-2">
            <Checkbox color="secondary" isSelected={sendCancellationEmail} onValueChange={() => setSendCancellationEmail(!sendCancellationEmail)}>Email {noOfInvitationsSent} users who received an invite</Checkbox>

            <Button size="sm" variant="flat" color="secondary" isDisabled={isPreviewEmailButtonDisabled} onPress={() => handlePreviewEmail(intro)} isLoading={isLoadingEmailPreview}>
              Preview
            </Button>
          </div>

          <Textarea
            classNames={{
              "inputWrapper": "bg-gray-100 border-0 !h-64",
              "input": "bg-gray-100",
            }}
            label="Email text"
            minRows={12}
            isDisabled={!sendCancellationEmail}
            isInvalid={!isFormValid}
            placeholder="Enter your description"
            value={intro}
            variant="bordered"
            onValueChange={setIntro}
            errorMessage="The message should be between 150 and 1000 characters long."
          />

          <Divider className="my-4" />
        </div>
      )}

      <Button 
        className="font-semibold" 
        color="danger" 
        fullWidth 
        isDisabled={isDeleteEventButtonDisabled} 
        onPress={() => handleDeleteEvent(isCancellationEmailShown)} isLoading={isDeletingEvent}
      >
        Delete Event
      </Button>
      
      {backButton}
      
      {showEmailPreviewModal && !previewEmailError && (
        <EmailPreviewModal 
          handleClose={() => setShowEmailPreviewModal(false)}
          html={previewEmailData?.previewEventCancelledEmail?.html}
          isLoading={isLoadingEmailPreview} 
          show={true}
          title="Delete Event Email Preview"
        />  
      )}
    </div>
  )
}

DeleteEvent.propTypes = {
  backButton: PropTypes.element.isRequired,
  event: PropTypes.object.isRequired,
}

export default DeleteEvent